import { useCallback, useEffect, useState } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { cn } from '../utils/cn';
import { glassFocusRing } from './GlassSurface';
import { springSnappy } from './motion';

export type GlassMode = 'light' | 'dark';

export const GLASS_MODE_STORAGE_KEY = 'aurora-glass-mode';

/**
 * Light is the primary mode.
 *
 * The theme was first authored dark, but this is an enterprise product used in
 * daylit offices and shown on projectors, and light is what people asked for.
 * The two are now equal citizens rather than a theme and its inversion — see
 * the light block in `glass.css`, which is a different construction rather than
 * flipped values.
 *
 * A stated OS preference still wins; light is only the fallback when the user
 * has expressed nothing.
 */
export function readGlassMode(storageKey: string = GLASS_MODE_STORAGE_KEY): GlassMode {
  if (typeof document === 'undefined') return 'light';
  const current = document.documentElement.dataset['mode'];
  if (current === 'light' || current === 'dark') return current;
  try {
    const stored = window.localStorage.getItem(storageKey);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    // Private browsing and blocked storage are not failures worth surfacing.
  }
  if (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  ) {
    return 'dark';
  }
  return 'light';
}

export function applyGlassMode(mode: GlassMode, storageKey: string = GLASS_MODE_STORAGE_KEY): void {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset['mode'] = mode;
  try {
    window.localStorage.setItem(storageKey, mode);
  } catch {
    // See above.
  }
}

export interface ThemeToggleProps {
  storageKey?: string;
  onChange?: (mode: GlassMode) => void;
  className?: string;
  label?: string;
}

function SunGlyph() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </svg>
  );
}

function MoonGlyph() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 14.5A8.5 8.5 0 019.5 4a8.5 8.5 0 1010.5 10.5z" />
    </svg>
  );
}

/**
 * Flips `data-mode` on the document element, which is the single switch the
 * whole glass theme keys off.
 */
export function ThemeToggle({
  storageKey = GLASS_MODE_STORAGE_KEY,
  onChange,
  className,
  label = 'Dark mode',
}: ThemeToggleProps) {
  const reduceMotion = useReducedMotion();
  const [mode, setMode] = useState<GlassMode>('light');

  // Read after mount rather than during render: the DOM and localStorage are
  // the source of truth, and neither exists while the tree is being built.
  useEffect(() => {
    const initial = readGlassMode(storageKey);
    setMode(initial);
    applyGlassMode(initial, storageKey);
  }, [storageKey]);

  const toggle = useCallback(() => {
    setMode((previous) => {
      const next: GlassMode = previous === 'dark' ? 'light' : 'dark';
      applyGlassMode(next, storageKey);
      onChange?.(next);
      return next;
    });
  }, [storageKey, onChange]);

  const dark = mode === 'dark';

  return (
    <button
      type="button"
      role="switch"
      aria-checked={dark}
      aria-label={label}
      onClick={toggle}
      className={cn(
        'relative inline-flex h-8 w-[60px] shrink-0 items-center rounded-full p-1',
        glassFocusRing,
        className,
      )}
      style={{
        background: 'color-mix(in oklab, var(--g-text) 8%, transparent)',
        border: '1px solid var(--g-edge)',
        boxShadow: 'var(--g-specular)',
      }}
    >
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 flex items-center justify-between px-2"
        style={{ color: 'var(--g-text-faint)' }}
      >
        <MoonGlyph />
        <SunGlyph />
      </span>

      <motion.span
        aria-hidden="true"
        className="relative flex h-6 w-6 items-center justify-center rounded-full"
        animate={{ x: dark ? 0 : 28 }}
        transition={reduceMotion ? { duration: 0 } : springSnappy}
        style={{
          background: 'var(--g-surface-solid)',
          border: '1px solid var(--g-edge-bright)',
          boxShadow: 'var(--g-shadow-sm)',
          color: 'var(--g-text)',
        }}
      >
        {dark ? <MoonGlyph /> : <SunGlyph />}
      </motion.span>
    </button>
  );
}
